import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import {
  formatDateTo24HourTimeString,
  stringToDate,
} from '../../../../../utils/time';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);

const MS_PER_HOUR = 60 * 60 * 1000;
const MAX_WARNING_HOURS = 24;

export type OfferHoursRemainingParams = {
  shiftDate: string;
  postedStart: string;
  /**
   * IANA timezone for the shift location (e.g. job.timeZone).
   * When omitted, wall-clock times are interpreted in the browser's local timezone.
   */
  timeZone?: string | null;
  /** Injectable "now" for tests. Defaults to current time. */
  now?: Date | string | number;
};

/**
 * Hours until shift start for the Full Shift Offer short-notice warning.
 * hoursRemaining = ceil((shiftStart − now) in hours), clamped to a minimum of 1.
 * Returns null when the warning should not show (≤ 0 or > 24 hours remaining).
 */
export function getOfferHoursRemaining({
  shiftDate,
  postedStart,
  timeZone,
  now,
}: OfferHoursRemainingParams): number | null {
  if (!shiftDate || !postedStart) {
    return null;
  }

  const datePart = String(shiftDate).replace(/\//g, '-').slice(0, 10);
  const timePart = formatDateTo24HourTimeString(
    stringToDate(postedStart, datePart)
  );
  const wallClock = `${datePart} ${timePart}`;

  // Prefer location timezone when the job payload includes it; otherwise fall back
  // to browser-local interpretation of the wall-clock start time.
  const shiftStart = timeZone
    ? dayjs.tz(wallClock, 'YYYY-MM-DD HH:mm', timeZone)
    : dayjs(wallClock, 'YYYY-MM-DD HH:mm');

  if (!shiftStart.isValid()) {
    return null;
  }

  const nowDj = now === undefined ? dayjs() : dayjs(now);
  const diffMs = shiftStart.valueOf() - nowDj.valueOf();

  if (diffMs <= 0) {
    return null;
  }

  const hoursRemaining = Math.max(1, Math.ceil(diffMs / MS_PER_HOUR));
  if (hoursRemaining > MAX_WARNING_HOURS) {
    return null;
  }

  return hoursRemaining;
}

export function formatShortNoticeOfferWarning(hoursRemaining: number): string {
  const unit = hoursRemaining === 1 ? 'hour' : 'hours';
  return (
    `This shift starts in ${hoursRemaining} ${unit}, it is your responsibility ` +
    `to check to see if it gets accepted. Sometimes these offers get accepted overnight. ` +
    `Remove your offer if you can't commit to ensuring you will show up if it gets accepted at any point.`
  );
}
